import form, { FIRST_ENABLED, FIRST_TEXT, FIRST_EMPTY, FIRST_ERROR } from './form.js';
import table, { replaceIndex, REGEX } from './table.js';
import dialog from './dialog.js';
import msgbox, { notify } from './msgbox.js';
import { makeOptions } from './record.js';
import db from './db.js';
import { findByName } from './misc.js';

const listTablesFromIndexedFields = function(descriptor)
{

	let tables = {};

	descriptor.fields.forEach(field =>
	{

		let field_selector = field.properties.selector.match(REGEX);

		if (!field_selector || field_selector.length < 4)		// [ 'field-0', 'field', '-', '0' ] 
			return;

		let row_selector = makeRowSelector(field_selector[0]);

		if (!tables[row_selector])
			tables[row_selector] = { table: null, fields: [] };

		if (tables[row_selector].fields.indexOf(field_selector[0]) == -1)
			tables[row_selector].fields.push(field_selector[0]);

	});

	return tables;

};

const makeRowSelector = function(index)
{
	let dashes = index.match(/-/g).length;
	let letters = index.match(/[A-Z]/g);
	return '.' + 'sub'.repeat(dashes - 1) + 'row' + (letters ? '-' + letters.join('') : '');
};

const interceptCallbacks = function(formset)
{

	formset._init = formset.form.init;
	formset._check = formset.form.check;
	formset._error = formset.form.error;
	formset._submit = formset.form.submit;

	formset.form.init = formInit.bind(formset);
	formset.form.check = formCheck.bind(formset);
	formset.form.error = formError.bind(formset);
	formset.form.submit = formSubmit.bind(formset);

};

const formInit = function(autorow = true)
{

	const formRoot = this.dialog?.getNode?.()?.querySelector(this.form.selector) || document.querySelector(this.form.selector);
	if (formRoot)
		this.form.node = formRoot;

	if (autorow && formRoot)
		formRoot.addEventListener('change', autoRow.bind(this));

	Object.keys(this.tables).forEach(selector =>
	{
		this.tables[selector].table = table.create(this.form, selector);
	});

	this._init.call(this.form, this);

};

const autoRow = function(ev)
{
	let root = this.dialog.getNode();
	let tables = Object.keys(this.tables);
	let table_selector;
	for (let node = ev.target; !table_selector && node != root; node = node.parentNode)
		table_selector = tables.find(table => table.substr(1) == node.className);
	let rows = root.querySelectorAll(table_selector);
	let in_last_row = rows[rows.length - 1]?.contains(ev.target);
	let is_checkbox = /checkbox|radio/.test(ev.target.getAttribute('type'));
	if (table_selector && in_last_row && !is_checkbox)
		this.appendRows(1, table_selector);
};

const formCheck = function()
{
	this._check.call(this.form, this);
};

const formError = function(message)
{
	this.form.setCursor(FIRST_ERROR);
	notify(message, 'Dados Incorretos');
};

const formSubmit = function(ev)
{
	// Formulário com submitHandler próprio (ex.: Transportadores) não usa validação/getValues genéricos
	if (typeof this.form.submitHandler === 'function')
	{
		try
		{
			this.form.submitHandler(this, ev);
		}
		catch (e)
		{
			formError.call(this, e.message);
			console.log(e);
		}
		finally
		{
			if (document.querySelector(this.form.selector))
				enableAllEmptyRows.call(this);
		}
		return;
	}
	try
	{
		ev?.preventDefault();
		disableAllEmptyRows.call(this, ['#filter', '#stocks-report-form'].includes(this.form.selector));
		// Valores ficam em sessionStorage e só sincronizam do DOM em blur/change; sem isso, Salvar com foco no campo perde o dado (ex.: empenho, tipo recebimento).
		this.form.syncFromDom();
		formCheck.call(this);
		let data = this.form.getValues();
		this.callback?.call(this, data);
	}
	catch (e)
	{
		formError.call(this, e.message);
		console.log(e);
	}
	finally
	{
		if (document.querySelector(this.form.selector))
			enableAllEmptyRows.call(this);
	}
};

const disableAllEmptyRows = function(allow_all_empty = false)
{
	Object.keys(this.tables).forEach(selector =>
	{
		disableEmptyRows(this.tables[selector], this.form, this.dialog.getNode().querySelectorAll(selector), allow_all_empty);
	});
};

const enableAllEmptyRows = function()
{
	Object.keys(this.tables).forEach(selector =>
	{
		enableEmptyRows(this.tables[selector], this.form, this.dialog.getNode().querySelectorAll(selector));
	});
};

const disableEmptyRows = function(table, form, rows, allow_all_empty)
{
	let indices = [];
	rows.forEach((row, index) =>
	{
		if (isRowEmpty(table, form, index))
			indices.push(index);
	});
	if (indices.length < rows.length || allow_all_empty)
		indices.forEach(index => disableRow(table, form, index));
};

const enableEmptyRows = function(table, form, rows)
{
	rows.forEach((row, index) =>
	{
		if (isRowEmptyAndDisabled(table, form, index))
			enableRow(table, form, index);
	});
};

const getEnabledRowsIndices = function(table, form, rows)
{
	let indices = [];
	rows.forEach((row, index) =>
	{
		if (!isRowEmptyAndDisabled(table, form, index))
			indices.push(index);
	});
	return indices;
};

const isRowEmpty = function(table, form, index)
{
	return table.fields.every(selector => form.getFieldBySelector(replaceIndex(selector, index)).isEmpty());
};

const isRowEmptyAndDisabled = function(table, form, index)
{
	return table.fields.every(selector =>
	{
		let field = form.getFieldBySelector(replaceIndex(selector, index));
		return field.isEmpty() && field.isDisabled();
	});
};

const disableRow = function(table, form, index)
{
	table.fields.forEach(selector => form.getFieldBySelector(replaceIndex(selector, index)).disableInput());
};

const enableRow = function(table, form, index)
{
	table.fields.forEach(selector => form.getFieldBySelector(replaceIndex(selector, index)).enableInput());
};

const clearRow = function(table, form, index)
{
	table.fields.forEach(selector =>
	{
		let field = form.getFieldBySelector(replaceIndex(selector, index));
		field.eraseValue();
		field.setErrorState(false);
		field.refresh();
	});
};

const dialogHandler = function(key, source)
{
	let table = source.getAttribute('data-table') || '.row';
	switch (key)
	{
		case 'Invert':
			this.invertSelection(table);
			break;
		case 'Clear':
			this.clearSelectedRows(table);
			break;
		case 'ResetFilter':
			this.handler?.(key);
			break;
		case 'Insert':
			this.appendRows(1, table);
			break;
		case 'Remove':
			this.removeSelectedRows(table);
			break;
		case 'MoveUp':
			this.moveRows(-1, table);
			break;
		case 'MoveDn':
			this.moveRows(+1, table);
			break;
		case 'Escape':
			this.dialog.destroy();
			break;
		case 'Enter':
			this.form.submit();
			break;
		default:
			this.handler?.call(this, key);
	}
};

const getCheckboxes = function(formset, table)
{
	return formset.dialog.getNode().querySelectorAll((table ? `${table} ` : '') + 'INPUT[id^="checkbox-"]');
};

const collectSelectedIndices = function(formset, table)
{
	let indices = [];
	getCheckboxes(formset, table).forEach((checkbox, index) =>
	{
		if (checkbox.checked)
			indices.push(index);
	});
	return indices;
};

const loadOptions = function(selector, options, value, labels, separator, formSelector = null)
{
	let node = formSelector
		? document.querySelector(`${formSelector} ${selector}`)
		: document.querySelector(selector);
	if (!node)
	{
		console.log('Not found:', selector);
		return;
	}
	let list_name = node.getAttribute('list');
	let index = node.id.match(REGEX);
	if (index)
	{
		list_name = replaceIndex(list_name || `${index[1]}_list-0`, index[3]);
		node.setAttribute('list', list_name);
	}
	let datalist = document.getElementById(list_name);
	if (!datalist)
	{
		datalist = document.createElement('datalist');
		datalist.setAttribute('id', list_name);
		// Listas compartilhadas por linhas indexadas (ex.: operators/connectors)
		// não podem ser inseridas dentro da <tr>, senão são clonadas com IDs duplicados.
		const is_indexed_list = Boolean(list_name?.match(REGEX));
		let parent_node = index && is_indexed_list ? node.parentNode : findByName(node, 'form');
		parent_node.insertBefore(datalist, parent_node.firstChild);
	}
	let is_static_list = options.length && Object.keys(options[0]).every(key => ['value', 'label'].includes(key));
	if (is_static_list)
		datalist.innerHTML = makeOptions(options, 'value', ['label']);
	else
		datalist.innerHTML = makeOptions(options, value || 'id', labels, separator);
};

const formset = {

	tables: {},
	dialog: null,
	form: null,
	callback: null,
	handler: null,

	create(descriptor, callback, handler)		// descriptor: { html, form, fields }, callback: optional, handler: optional
	{

		let formset = Object.create(this);

		formset.tables = listTablesFromIndexedFields(descriptor);
		formset.dialog = dialog.create(descriptor.html, dialogHandler.bind(formset));
		formset.form = form.create(descriptor.form, descriptor.fields, callback);
		formset.callback = callback;
		formset.handler = handler;

		interceptCallbacks(formset);

		return formset;

	},

	destroy()
	{
		this.dialog.destroy();
	},

	invertSelection(table = '.row')
	{
		getCheckboxes(this, table).forEach(checkbox => checkbox.click());
	},

	/** Índices das linhas com checkbox marcado (0-based na tabela). Usado por CRUDs (ex.: saídas). */
	getSelectedIndices(table = '.row')
	{
		return collectSelectedIndices(this, table);
	},

	clearAllRows(table = '.row')
	{
		getCheckboxes(this, table).forEach((checkbox, index) => clearRow(this.tables[table], this.form, index));
	},

	clearSelectedRows(table = '.row')
	{
		let indices = collectSelectedIndices(this, table);
		indices.forEach(index => clearRow(this.tables[table], this.form, index));
	},

	appendRows(count, table = '.row')
	{
		const slot = this.tables[table];
		if (!slot?.table)
		{
			console.error('formset.appendRows: tabela não foi inicializada (provável falha em formInit).', table);
			return;
		}
		slot.table.appendRows(count);
	},

	removeRows(indices, table = '.row')
	{
		this.tables[table].table.removeRows(indices);
	},

	removeSelectedRows(table = '.row')
	{
		let indices = collectSelectedIndices(this, table);
		this.tables[table].table.removeRows(indices);
	},

	moveRows(count, table = '.row')
	{
		let indices = collectSelectedIndices(this, table);
		if (count > 0)
			indices.reverse();		// If moving down, start moving the bottom rows first
		indices.every(index => this.tables[table].table.moveRow(index, count));
	},

	getEnabledRowsIndices(table = '.row')
	{
		return getEnabledRowsIndices(this.tables[table], this.form, this.dialog.getNode().querySelectorAll(table));
	},

	hasTables()
	{
		return Object.keys(this.tables).length > 0;
	},

	getTableSize(table = '.row')
	{
		const root = this.dialog.getNode();
		const rows = root.querySelectorAll(table);
		const declared = rows[0]?.getAttribute('data-size');
		if (declared != null && declared !== '')
		{
			const n = parseInt(declared, 10);
			if (!Number.isNaN(n) && n > 0)
				return n;
		}
		// Sem data-size (ex.: modal #filter): contar linhas reais no DOM, não assumir 6.
		return rows.length || 1;
	},

	setTableHeight(rows, table = '.row')
	{
		let node = this.dialog.getNode().querySelector(`.form *:has(> table ${table})`);
		if (!node)
			return;
		// Grade padrão SIAL: altura via CSS (--sial-items-h); não sobrescrever com inline.
		if (node.classList.contains('sial-items-scroll'))
		{
			node.style.minHeight = '';
			node.style.maxHeight = '';
			node.style.height = '';
			node.style.overflowY = '';
			return;
		}
		node.style.minHeight = (rows * 2.4) + 'rem';
		node.style.maxHeight = (rows * 2.4 + 1.2) + 'rem';
	},

	async loadList(datalist, store, options)	// options: { range:, index:, value:, labels:, separator: }
	{
		let node = document.querySelector(`${this.form.selector} ${datalist}`);
		if (!node)
			return;
		let data = await db.list(store, options?.range, options?.index);
		if (store === 'warehouses' && data)
			data = data.filter((r) => !(r.is_disabled === true || r.is_disabled === 1 || r.is_disabled === '1'));
		node.innerHTML = data ? makeOptions(data, options?.value ?? 'id', options?.labels ?? ['name'], options?.separator) : '';
		this.form.refresh();	// Better to refresh after each load than have to await each individual load and then refresh once
	},

	init(autorow)
	{
		try
		{
			formInit.call(this, autorow);
		}
		catch (e)
		{
			console.error(e);
			const detail = e?.message || String(e);
			notify(
				`${detail}\n\n` +
					'Isso costuma indicar HTML e a lista fields fora de sincronia (id de coluna sem campo correspondente).',
				'Erro ao preparar o formulário'
			);
		}
	},

	submit()
	{
		this.form.submit();
	},

};

export { formset as default, loadOptions, FIRST_ENABLED, FIRST_TEXT, FIRST_EMPTY, FIRST_ERROR };

